import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getApiUrl } from '../../utils/api';
import { formatBytes, formatDate } from '../../utils/formatters';

// --- Types ---

interface StorageItem {
  name: string;
  path: string;
  type: 'directory' | 'file';
  size?: number;
  last_modified?: string;
  etag?: string;
  s3_key?: string;
  bucket?: string;
}

interface StorageUsage {
  tenant_id: string;
  quota_gb: number;
  used_gb: number;
  used_bytes: number;
  object_count: number;
  storage_tier: string;
  last_sync: string | null;
  recent_transactions: Array<{
    operation: string;
    file_path: string;
    size: number;
    timestamp: string;
    bucket: string;
  }>;
}

interface FileExplorerProps {
  onFileSelect?: (file: StorageItem) => void;
  showUpload?: boolean;
  showUsage?: boolean;
}

// --- Helpers ---

const getToken = () => localStorage.getItem('token') || localStorage.getItem('authToken');

const sortItems = (items: StorageItem[]): StorageItem[] => {
  return [...items].sort((a, b) => {
    if (a.type === 'directory' && b.type !== 'directory') return -1;
    if (a.type !== 'directory' && b.type === 'directory') return 1;
    return a.name.localeCompare(b.name);
  });
};

// --- Icons ---

const FolderClosedIcon = () => (
  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
  </svg>
);

const FolderOpenIcon = () => (
  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H8a3 3 0 00-3 3v1.5a1.5 1.5 0 01-3 0V6z" clipRule="evenodd" />
    <path d="M6 12a2 2 0 012-2h8a2 2 0 012 2v2a2 2 0 01-2 2H2h2a2 2 0 002-2v-2z" />
  </svg>
);

const DirChevron = ({ isExpanded, isLoading }: { isExpanded: boolean; isLoading: boolean }) => {
  if (isLoading) {
    return (
      <svg className="w-3 h-3 animate-spin text-gray-500 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  }
  return (
    <svg
      className={`w-3 h-3 text-gray-500 mr-1 flex-shrink-0 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
};

const getFileIcon = (name: string) => {
  const ext = name.split('.').pop()?.toLowerCase() || '';

  if (ext === 'ipynb') {
    return (
      <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  if (['py', 'js', 'ts', 'jsx', 'tsx'].includes(ext)) {
    return (
      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    );
  }
  if (['csv', 'json', 'parquet', 'xlsx', 'xls'].includes(ext)) {
    return (
      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    );
  }
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'].includes(ext)) {
    return (
      <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
};

// --- Component ---

export const FileExplorer: React.FC<FileExplorerProps> = ({
  onFileSelect,
  showUpload = true,
  showUsage = false,
}) => {
  // Navigation
  const [currentPath, setCurrentPath] = useState('');
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  // Items
  const [items, setItems] = useState<StorageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState<number>(-1);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: StorageItem } | null>(null);

  // Inline rename
  const [renamingFile, setRenamingFile] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const renameInitTimestamp = useRef<number>(0);

  // Inline delete
  const [deleteConfirmFile, setDeleteConfirmFile] = useState<string | null>(null);

  // New folder dialog
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Drag-and-drop
  const [draggedItem, setDraggedItem] = useState<StorageItem | null>(null);
  const [dropTargetDir, setDropTargetDir] = useState<string | null>(null);

  // Tree expansion
  const [expandedDirs, setExpandedDirs] = useState<Record<string, StorageItem[]>>({});
  const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set());

  // Storage usage
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);

  const fileListRef = useRef<HTMLDivElement>(null);

  // --- Data fetching ---

  const fetchItems = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const params = new URLSearchParams();
      if (path) params.append('path', path);

      const response = await fetch(`${getApiUrl()}/api/v1/storage/browse?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch files');

      const data = await response.json();

      // Merge directories and files into single sorted list
      const dirs: StorageItem[] = (data.directories || []).map((d: any) => ({
        ...d,
        type: 'directory' as const,
      }));
      const files: StorageItem[] = (data.files || []).map((f: any) => ({
        ...f,
        type: 'file' as const,
      }));

      setItems(sortItems([...dirs, ...files]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStorageUsage = useCallback(async () => {
    if (!showUsage) return;
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(`${getApiUrl()}/api/v1/storage/usage`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        setStorageUsage(await response.json());
      }
    } catch (err) {
      console.error('Error fetching storage usage:', err);
    }
  }, [showUsage]);

  useEffect(() => {
    fetchItems(currentPath);
    fetchStorageUsage();
  }, [currentPath, fetchItems, fetchStorageUsage]);

  const handleRefresh = useCallback(() => {
    setExpandedDirs({});
    setLoadingDirs(new Set());
    fetchItems(currentPath);
    fetchStorageUsage();
  }, [currentPath, fetchItems, fetchStorageUsage]);

  // --- Fetch children for a single directory (for inline expansion) ---

  const fetchDirChildren = useCallback(async (dirPath: string): Promise<StorageItem[]> => {
    const token = getToken();
    if (!token) return [];

    const params = new URLSearchParams();
    if (dirPath) params.append('path', dirPath);

    const response = await fetch(`${getApiUrl()}/api/v1/storage/browse?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) return [];

    const data = await response.json();
    const dirs: StorageItem[] = (data.directories || []).map((d: any) => ({
      ...d, type: 'directory' as const,
    }));
    const files: StorageItem[] = (data.files || []).map((f: any) => ({
      ...f, type: 'file' as const,
    }));
    return sortItems([...dirs, ...files]);
  }, []);

  // --- Toggle expand/collapse for a directory (single-click) ---

  const toggleDir = useCallback(async (dirPath: string) => {
    // If already expanded, collapse (and all children)
    if (expandedDirs[dirPath] !== undefined) {
      setExpandedDirs(prev => {
        const next = { ...prev };
        delete next[dirPath];
        for (const key of Object.keys(next)) {
          if (key.startsWith(dirPath + '/')) delete next[key];
        }
        return next;
      });
      return;
    }

    // Expand: fetch children
    setLoadingDirs(prev => { const n = new Set(prev); n.add(dirPath); return n; });
    try {
      const children = await fetchDirChildren(dirPath);
      setExpandedDirs(prev => ({ ...prev, [dirPath]: children }));
    } catch (err) {
      console.error('Failed to load directory:', err);
      setError(`Failed to load ${dirPath}`);
    } finally {
      setLoadingDirs(prev => { const n = new Set(prev); n.delete(dirPath); return n; });
    }
  }, [expandedDirs, fetchDirChildren]);

  // --- Navigation ---

  const navigateTo = (path: string) => {
    if (path !== currentPath) {
      setPathHistory(prev => [...prev, currentPath]);
      setCurrentPath(path);
      setSelectedFiles(new Set());
      setLastClickedIndex(-1);
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  const navigateBack = () => {
    if (pathHistory.length > 0) {
      const previous = pathHistory[pathHistory.length - 1];
      setPathHistory(prev => prev.slice(0, -1));
      setCurrentPath(previous);
      setSelectedFiles(new Set());
      setLastClickedIndex(-1);
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  const navigateToRoot = () => {
    if (currentPath !== '') {
      setPathHistory([]);
      setCurrentPath('');
      setSelectedFiles(new Set());
      setLastClickedIndex(-1);
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  // --- Selection ---

  // Find an item across root items and all expanded dirs
  const findItem = useCallback((path: string): StorageItem | undefined => {
    const found = items.find(f => f.path === path);
    if (found) return found;
    for (const children of Object.values(expandedDirs)) {
      const child = children.find(f => f.path === path);
      if (child) return child;
    }
    return undefined;
  }, [items, expandedDirs]);

  const handleItemClick = (item: StorageItem, _index: number, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      // Multi-select toggle
      const newSelection = new Set(selectedFiles);
      if (newSelection.has(item.path)) {
        newSelection.delete(item.path);
      } else {
        newSelection.add(item.path);
      }
      setSelectedFiles(newSelection);
      setLastClickedIndex(_index);
      return;
    }

    if (event.shiftKey && lastClickedIndex >= 0) {
      // Range select across root items only
      const start = Math.min(_index, lastClickedIndex);
      const end = Math.max(_index, lastClickedIndex);
      const newSelection = new Set<string>();
      for (let i = start; i <= end; i++) {
        newSelection.add(items[i].path);
      }
      setSelectedFiles(newSelection);
      return;
    }

    // Single click: dirs toggle expand, files select
    setSelectedFiles(new Set([item.path]));
    setLastClickedIndex(_index);

    if (item.type === 'directory') {
      toggleDir(item.path);
    } else {
      onFileSelect?.(item);
    }
  };

  // Double-click: dirs navigate, files same as single-click
  const handleItemDoubleClick = (item: StorageItem) => {
    if (item.type === 'directory') {
      navigateTo(item.path);
    }
  };

  // --- Upload ---

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const filePath = currentPath ? `${currentPath}/${file.name}` : file.name;

      // Step 1: Get presigned URL
      const uploadUrlResponse = await fetch(`${getApiUrl()}/api/v1/storage/upload-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_name: file.name,
          file_type: file.type || 'application/octet-stream',
          file_path: filePath,
          file_size: file.size,
        }),
      });

      if (!uploadUrlResponse.ok) throw new Error('Failed to get upload URL');

      const { url, key, tags } = await uploadUrlResponse.json();

      // Step 2: Upload directly to S3
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress((e.loaded / e.total) * 100);
        }
      });

      await new Promise((resolve, reject) => {
        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        if (tags) {
          xhr.setRequestHeader('x-amz-tagging', tags);
        }
        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 204) resolve(xhr.response);
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });

      // Step 3: Notify backend
      const params = new URLSearchParams({
        file_path: filePath,
        file_size: file.size.toString(),
        s3_key: key,
      });

      await fetch(`${getApiUrl()}/api/v1/storage/complete-upload?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // --- Download ---

  const handleDownload = async (item: StorageItem) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(`${getApiUrl()}/api/v1/storage/download-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_path: item.path }),
      });

      if (!response.ok) throw new Error('Failed to get download URL');
      const { url } = await response.json();
      window.open(url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    }
  };

  // --- Delete ---

  const handleDeleteConfirmed = async (itemsToDelete: StorageItem[]) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      for (const item of itemsToDelete) {
        await fetch(`${getApiUrl()}/api/v1/storage/files`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ file_path: item.path }),
        });
      }

      setSelectedFiles(new Set());
      setDeleteConfirmFile(null);
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // --- Rename ---

  const startRename = (item: StorageItem) => {
    renameInitTimestamp.current = Date.now();
    setRenamingFile(item.path);
    setRenameValue(item.name);
    setContextMenu(null);
  };

  const handleRenameSubmit = async (item: StorageItem) => {
    if (!renameValue.trim() || renameValue === item.name) {
      setRenamingFile(null);
      return;
    }

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const dir = item.path.substring(0, item.path.lastIndexOf('/') + 1);
      const newPath = dir + renameValue.trim();

      const response = await fetch(`${getApiUrl()}/api/v1/storage/rename`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ old_path: item.path, new_path: newPath }),
      });

      if (!response.ok) throw new Error('Rename failed');

      setRenamingFile(null);
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rename failed');
      setRenamingFile(null);
    }
  };

  // --- Create folder ---

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const folderPath = currentPath
        ? `${currentPath}/${newFolderName.trim()}`
        : newFolderName.trim();

      const response = await fetch(`${getApiUrl()}/api/v1/storage/create-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ folder_path: folderPath }),
      });

      if (!response.ok) throw new Error('Failed to create folder');

      setShowNewFolderDialog(false);
      setNewFolderName('');
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    }
  };

  // --- Copy path ---

  const handleCopyPath = (item: StorageItem) => {
    navigator.clipboard.writeText(item.path);
    setContextMenu(null);
  };

  // --- Drag and drop ---

  const handleDragStart = (e: React.DragEvent, item: StorageItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.path);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTargetDir(null);
  };

  const handleDragOver = (e: React.DragEvent, targetPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedItem) return;
    if (draggedItem.path === targetPath || targetPath.startsWith(draggedItem.path + '/')) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'move';
    setDropTargetDir(targetPath);
  };

  const handleDragLeave = (e: React.DragEvent, targetPath: string) => {
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (relatedTarget && (e.currentTarget as HTMLElement).contains(relatedTarget)) return;
    if (dropTargetDir === targetPath) setDropTargetDir(null);
  };

  const handleDrop = async (e: React.DragEvent, targetDirPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetDir(null);

    if (!draggedItem) return;
    if (draggedItem.path === targetDirPath || targetDirPath.startsWith(draggedItem.path + '/')) {
      setDraggedItem(null);
      return;
    }

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(`${getApiUrl()}/api/v1/storage/move`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_path: draggedItem.path,
          destination_dir: targetDirPath,
        }),
      });

      if (!response.ok) throw new Error('Move failed');
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move file');
    } finally {
      setDraggedItem(null);
    }
  };

  // --- Context menu close ---

  useEffect(() => {
    if (!contextMenu) return;
    const handleClose = () => setContextMenu(null);
    document.addEventListener('mousedown', handleClose);
    document.addEventListener('scroll', handleClose, true);
    return () => {
      document.removeEventListener('mousedown', handleClose);
      document.removeEventListener('scroll', handleClose, true);
    };
  }, [contextMenu]);

  // --- Focus rename input ---

  useEffect(() => {
    if (renamingFile && renameInputRef.current) {
      requestAnimationFrame(() => {
        if (!renameInputRef.current) return;
        renameInputRef.current.focus();
        const val = renameInputRef.current.value;
        const dotIndex = val.lastIndexOf('.');
        if (dotIndex > 0) {
          renameInputRef.current.setSelectionRange(0, dotIndex);
        } else {
          renameInputRef.current.select();
        }
      });
    }
  }, [renamingFile]);

  // --- Keyboard shortcuts ---

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (renamingFile || showNewFolderDialog) return;

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFiles.size > 0) {
        e.preventDefault();
        // Set delete confirm for first selected item
        const firstSelected = Array.from(selectedFiles)[0];
        setDeleteConfirmFile(firstSelected);
      } else if (e.key === 'F2' && selectedFiles.size === 1) {
        e.preventDefault();
        const path = Array.from(selectedFiles)[0];
        const item = findItem(path);
        if (item) startRename(item);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedFiles, findItem, renamingFile, showNewFolderDialog]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Context menu handler ---

  const handleContextMenu = (e: React.MouseEvent, item: StorageItem) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedFiles(new Set([item.path]));
    setContextMenu({ x: e.clientX, y: e.clientY, item });
  };

  // --- Breadcrumbs ---

  const pathParts = currentPath ? currentPath.split('/').filter(Boolean) : [];

  // --- Recursive tree renderer ---

  const renderFileTree = (treeItems: StorageItem[], depth: number) => {
    return treeItems.map((item, index) => {
      const isDir = item.type === 'directory';
      const isExpanded = expandedDirs[item.path] !== undefined;
      const isDirLoading = loadingDirs.has(item.path);
      const children = expandedDirs[item.path];
      const isDragged = draggedItem?.path === item.path;
      const isDropTarget = isDir && dropTargetDir === item.path;
      const isInvalidDropTarget = isDir && draggedItem && (
        draggedItem.path === item.path ||
        item.path.startsWith(draggedItem.path + '/')
      );

      return (
        <div key={item.path}>
          <button
            draggable={true}
            onDragStart={(e) => handleDragStart(e, item)}
            onDragEnd={handleDragEnd}
            {...(isDir ? {
              onDragOver: (e: React.DragEvent<HTMLButtonElement>) => handleDragOver(e, item.path),
              onDragLeave: (e: React.DragEvent<HTMLButtonElement>) => handleDragLeave(e, item.path),
              onDrop: (e: React.DragEvent<HTMLButtonElement>) => handleDrop(e, item.path),
            } : {})}
            onClick={(e) => {
              if (e.ctrlKey && e.button === 0) return;
              handleItemClick(item, index, e);
            }}
            onDoubleClick={() => handleItemDoubleClick(item)}
            onContextMenu={(e) => handleContextMenu(e, item)}
            className={`w-full flex items-center py-1.5 text-left transition-all border-l-2 ${
              isDragged
                ? 'opacity-50 text-gray-500 border-transparent'
                : isDropTarget
                  ? 'bg-blue-500/10 text-white border-blue-500 ring-1 ring-blue-500/50'
                  : isInvalidDropTarget
                    ? 'cursor-not-allowed border-transparent text-gray-300'
                    : selectedFiles.has(item.path)
                      ? 'bg-blue-500/10 text-white border-blue-500'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white border-transparent'
            }`}
            style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: '12px' }}
          >
            {isDir && (
              <DirChevron isExpanded={isExpanded} isLoading={isDirLoading} />
            )}
            {!isDir && (
              <span className="w-3 mr-1 flex-shrink-0" />
            )}
            <span className="mr-2 flex-shrink-0">
              {isDir ? (isExpanded ? <FolderOpenIcon /> : <FolderClosedIcon />) : getFileIcon(item.name)}
            </span>

            {renamingFile === item.path ? (
              <input
                ref={renameInputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameSubmit(item);
                  if (e.key === 'Escape') setRenamingFile(null);
                  e.stopPropagation();
                }}
                onBlur={() => {
                  if (Date.now() - renameInitTimestamp.current < 200) return;
                  handleRenameSubmit(item);
                }}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 text-sm bg-gray-800 text-white border border-blue-500/50 rounded px-1.5 py-0.5 outline-none min-w-0"
              />
            ) : (
              <span className="flex-1 text-sm truncate">{item.name}</span>
            )}

            {!isDir && item.size != null && renamingFile !== item.path && (
              <span className="text-xs text-gray-500 ml-2 flex-shrink-0 w-16 text-right">
                {formatBytes(item.size)}
              </span>
            )}

            {!isDir && item.last_modified && renamingFile !== item.path && (
              <span className="text-xs text-gray-600 ml-3 flex-shrink-0 w-24 text-right hidden sm:inline">
                {formatDate(item.last_modified)}
              </span>
            )}
          </button>

          {/* Inline delete confirmation */}
          {deleteConfirmFile === item.path && (
            <div
              className="flex items-center gap-2 py-1.5 bg-red-900/20 border-l-2 border-red-500"
              style={{ paddingLeft: `${12 + depth * 16}px`, paddingRight: '12px' }}
            >
              <span className="text-xs text-red-300 flex-1 truncate">
                {selectedFiles.size > 1
                  ? `Delete ${selectedFiles.size} items?`
                  : <>Delete &ldquo;{item.name}&rdquo;?</>
                }
              </span>
              <button
                onClick={() => {
                  if (selectedFiles.size > 1) {
                    const toDelete = items.filter(f => selectedFiles.has(f.path));
                    handleDeleteConfirmed(toDelete);
                  } else {
                    handleDeleteConfirmed([item]);
                  }
                }}
                className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-500 transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirmFile(null)}
                className="px-2 py-0.5 text-xs text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Render expanded children */}
          {isExpanded && children && children.length > 0 && renderFileTree(children, depth + 1)}
          {isExpanded && children && children.length === 0 && (
            <div
              className="text-xs text-gray-600 italic py-1"
              style={{ paddingLeft: `${28 + depth * 16}px` }}
            >
              Empty
            </div>
          )}
        </div>
      );
    });
  };

  // --- Render ---

  return (
    <div className="flex flex-col h-full bg-black/50 backdrop-blur-sm rounded-lg border border-gray-800/50">
      {/* Breadcrumb navigation */}
      <div className="flex-shrink-0 border-b border-gray-800/50">
        <div className="flex items-center px-3 py-2 bg-black/30 backdrop-blur-sm">
          <button
            onClick={navigateBack}
            disabled={pathHistory.length === 0}
            className={`mr-2 p-1.5 transition-all flex-shrink-0 ${
              pathHistory.length > 0
                ? 'text-gray-400 hover:text-white hover:bg-white/10'
                : 'text-gray-600 cursor-not-allowed'
            }`}
            title="Back"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex items-center text-sm min-w-0 overflow-hidden flex-1">
            <button
              onClick={navigateToRoot}
              className="text-gray-400 hover:text-white flex-shrink-0"
            >
              Home
            </button>
            {pathParts.map((part, index) => (
              <React.Fragment key={index}>
                <span className="mx-1 text-gray-600 flex-shrink-0">/</span>
                <button
                  onClick={() => {
                    const targetPath = pathParts.slice(0, index + 1).join('/');
                    navigateTo(targetPath);
                  }}
                  className="text-gray-400 hover:text-white truncate"
                >
                  {part}
                </button>
              </React.Fragment>
            ))}
          </div>

          {selectedFiles.size > 0 && (
            <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
              {selectedFiles.size} selected
            </span>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-end px-3 py-1 bg-black/20 border-t border-gray-800/30 space-x-1">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:text-gray-600 disabled:cursor-not-allowed"
            title="Refresh"
          >
            <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          <button
            onClick={() => setShowNewFolderDialog(true)}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all"
            title="New Folder"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
          </button>

          {showUpload && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:text-gray-600 disabled:cursor-not-allowed"
                title="Upload"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach(handleUpload);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              />
            </>
          )}

          <button
            onClick={() => {
              const item = items.find(f => selectedFiles.has(f.path) && f.type === 'file');
              if (item) handleDownload(item);
            }}
            disabled={!Array.from(selectedFiles).some(p => items.find(f => f.path === p && f.type === 'file'))}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:text-gray-600 disabled:cursor-not-allowed"
            title="Download"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>

          <button
            onClick={() => {
              if (selectedFiles.size > 0) {
                setDeleteConfirmFile(Array.from(selectedFiles)[0]);
              }
            }}
            disabled={selectedFiles.size === 0}
            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-white/10 transition-all disabled:text-gray-600 disabled:cursor-not-allowed"
            title="Delete"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div className="px-3 py-2 border-t border-gray-800/30">
            <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
              <span>Uploading...</span>
              <span>{Math.round(uploadProgress)}%</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1.5">
              <div
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Storage Usage */}
        {showUsage && storageUsage && (
          <div className="px-3 py-2 border-t border-gray-800/30">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Storage</span>
              <span className="text-gray-400">
                {formatBytes(storageUsage.used_bytes)} / {storageUsage.quota_gb} GB
              </span>
            </div>
            <div className="mt-1 w-full bg-gray-800 rounded-full h-1">
              <div
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-1 rounded-full"
                style={{ width: `${Math.min(100, (storageUsage.used_gb / storageUsage.quota_gb) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="px-3 py-2 border-t border-gray-800/30">
            <div className="flex items-start justify-between text-red-400 text-xs">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-2 text-red-400/60 hover:text-red-300 flex-shrink-0"
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* File list */}
      <div
        className={`flex-1 overflow-y-auto ${
          dropTargetDir === currentPath && draggedItem ? 'ring-1 ring-inset ring-blue-500/50 bg-blue-500/5' : ''
        }`}
        ref={fileListRef}
        onDragOver={(e) => {
          if (draggedItem) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropTargetDir(currentPath);
          }
        }}
        onDragLeave={(e) => {
          const relatedTarget = e.relatedTarget as HTMLElement | null;
          if (relatedTarget && (e.currentTarget as HTMLElement).contains(relatedTarget)) return;
          if (dropTargetDir === currentPath) setDropTargetDir(null);
        }}
        onDrop={(e) => handleDrop(e, currentPath)}
      >
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-gray-500 text-sm">Loading...</div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
            <svg className="w-12 h-12 mb-3 text-red-500/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-red-300">Couldn't load this folder</p>
            <p className="text-xs text-gray-500 mt-1">The listing request failed. Your files are not affected.</p>
            <button
              onClick={() => fetchItems(currentPath)}
              className="mt-4 px-3 py-1.5 text-xs bg-white/5 text-gray-300 hover:bg-white/10 border border-gray-700/30 transition-all rounded"
            >
              Retry
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
            <svg className="w-12 h-12 mb-3 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
            <p className="text-sm text-gray-300">Empty folder</p>
            <p className="text-xs text-gray-500 mt-1">Upload files or create a folder</p>
            <div className="flex gap-2 mt-4">
              {showUpload && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 text-xs bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-600/30 transition-all rounded"
                >
                  Upload
                </button>
              )}
              <button
                onClick={() => setShowNewFolderDialog(true)}
                className="px-3 py-1.5 text-xs bg-white/5 text-gray-400 hover:bg-white/10 border border-gray-700/30 transition-all rounded"
              >
                New Folder
              </button>
            </div>
          </div>
        ) : (
          <div className="py-1">
            {renderFileTree(items, 0)}
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && ReactDOM.createPortal(
        <div
          className="fixed z-[100] min-w-[160px] py-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.item.type === 'file' && (
            <button
              onClick={() => { handleDownload(contextMenu.item); setContextMenu(null); }}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
          )}
          <button
            onClick={() => handleCopyPath(contextMenu.item)}
            className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
            </svg>
            Copy Path
          </button>
          <button
            onClick={() => startRename(contextMenu.item)}
            className="w-full px-3 py-1.5 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Rename
          </button>
          <div className="border-t border-gray-700 my-1" />
          <button
            onClick={() => { setDeleteConfirmFile(contextMenu.item.path); setContextMenu(null); }}
            className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-red-900/30 hover:text-red-300 flex items-center gap-2"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>,
        document.body
      )}

      {/* New folder dialog */}
      {showNewFolderDialog && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-black/90 backdrop-blur-md p-6 w-96 border border-gray-800/50 shadow-2xl rounded-lg">
            <h3 className="text-white font-semibold mb-4 text-lg">New Folder</h3>

            <div className="mb-3 text-xs text-gray-500">
              Location: <span className="text-gray-400 font-mono">{currentPath || '/'}</span>
            </div>

            <div className="mb-6">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className="w-full px-3 py-2 bg-black/50 text-white border border-gray-700/50 rounded focus:border-blue-500 focus:outline-none backdrop-blur-sm transition-all"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateFolder();
                  if (e.key === 'Escape') {
                    setShowNewFolderDialog(false);
                    setNewFolderName('');
                  }
                }}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewFolderDialog(false);
                  setNewFolderName('');
                }}
                className="px-4 py-2 text-gray-400 hover:text-white bg-gray-800/30 hover:bg-gray-800/50 border border-gray-700/50 rounded backdrop-blur-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 bg-blue-600/80 text-white hover:bg-blue-600 rounded disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm transition-all shadow hover:shadow-blue-500/20"
              >
                Create
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default FileExplorer;
